import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataQueryKey } from '../../../hooks/useDataQuery/dataQuery.types';
import useHandleBrandContextInSessionStorage from '../../../hooks/useHandleBrandContextInSessionStorage';
import { usePageTitleManager } from '../../../hooks/usePageTitleManager';
import { useGetOrderById } from '../../../services/modules/order/order.queries';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import { WithId } from '../../../types/Base.types';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { getEnabledQueryBasedOnOfflineMode } from '../../offline/queryClient/utils/offlineDataQuery.utils';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import useOrderBook from '../../order-book/hooks/useOrderBook';
import useCanEditSuggestedOrder from '../hooks/useCanEditSuggestedOrder';
import { Order } from '../order.types';
import { CopiedGrid } from '../orderState.types';
import { OrderProductsViewActionsEnum } from '../products/orderProducts.types';
import { useHandleOfflineOrderSyncingToOrder } from './hooks/useHandleOfflineOrderSyncingToOrder';

const initialCopiedGridState: CopiedGrid = { grid: [], product: undefined, orderId: '' };

export const OrderContext = createContext<OrderContextProps>({
  reviewAction: OrderProductsViewActionsEnum.EXPAND_ALL,
  editSuggestedOrderRule: false,
  order: undefined,
  copiedGrid: initialCopiedGridState,
  canEditOrder: false,
  copyGrid: () => {
    return;
  },
  removeCopiedGrid: () => {
    return;
  },
  changeOrderReviewAction: () => {
    return;
  },
});

interface OrderContextProviderProps extends UsedInOfflineMode {
  children: React.ReactNode;
}

export const OrderContextProvider = ({ children, isOfflineMode }: OrderContextProviderProps) => {
  const params = useParams<WithId>();
  const queryClient = useQueryClient();

  const { erpIntegration, hasBrandIntegratorIntegration, hasHopeIntegration, team } =
    useApplicationContext();

  const { selectedOrderIsSuggested } = useOrderBook();
  const selectedOrder = useOrderBookStore((state) => state.selectedOrder);
  const roleCanEditSuggestedOrder = useCanEditSuggestedOrder();

  const [copiedGrid, setCopiedGrid] = useState<CopiedGrid>(initialCopiedGridState);
  const [reviewAction, setReviewAction] = useState(OrderProductsViewActionsEnum.EXPAND_ALL);

  const { data: orderData, refetch } = useGetOrderById({
    enabled: getEnabledQueryBasedOnOfflineMode(isOfflineMode),
    id: params.id,
  });

  useHandleBrandContextInSessionStorage({ brandId: orderData?.brand?.id });

  usePageTitleManager({
    dynamicPart: orderData?.code,
  });

  useHandleOfflineOrderSyncingToOrder({
    order: orderData,
    reloadCurrentOrder: () => refetch({ ignoreOnlineValidation: true }),
  });

  const canEditSuggestedOrder =
    selectedOrder?.id === orderData?.id &&
    selectedOrderIsSuggested &&
    orderData?.status === 'SUGGESTED' &&
    !orderData?.archived &&
    roleCanEditSuggestedOrder;

  const hasApiOrHopeIntegration = hasBrandIntegratorIntegration || hasHopeIntegration;
  const isOrderInIntegrationStatusTrigger =
    erpIntegration?.integrationTrigger === orderData?.status;

  const blockEditOnTrigger = hasApiOrHopeIntegration && isOrderInIntegrationStatusTrigger;
  const canEditOrder =
    (canEditSuggestedOrder || orderData?.status !== 'SUGGESTED') && !blockEditOnTrigger;
  const copyGrid = useCallback((grid: CopiedGrid) => {
    setCopiedGrid({ ...grid });
  }, []);

  const removeCopiedGrid = useCallback(() => {
    setCopiedGrid(initialCopiedGridState);
  }, []);

  const changeOrderReviewAction = useCallback((newReviewAction: OrderProductsViewActionsEnum) => {
    setReviewAction(newReviewAction);
  }, []);

  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [team?.id, DataQueryKey.PRODUCT_GRID],
      });
    },
    [params.id, queryClient, team?.id],
  );

  useEffect(() => {
    if (copiedGrid.product) {
      removeCopiedGrid();
    }
  }, [selectedOrder?.id]);

  const value: OrderContextProps = {
    reviewAction,
    order: orderData,
    copyGrid,
    removeCopiedGrid,
    copiedGrid,
    changeOrderReviewAction,
    canEditOrder,
    editSuggestedOrderRule: canEditSuggestedOrder,
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

interface OrderContextProps {
  reviewAction: OrderProductsViewActionsEnum;
  order?: Order<true | false>;
  copiedGrid: CopiedGrid;
  canEditOrder: boolean;
  editSuggestedOrderRule: boolean;
  copyGrid: (grid: CopiedGrid) => void;
  removeCopiedGrid: () => void;
  changeOrderReviewAction: (reviewAction: OrderProductsViewActionsEnum) => void;
}

export const useOrderContext = () => useContext<OrderContextProps>(OrderContext);
